import { useState, useEffect, useCallback, useRef } from "react";
import { MOCK } from "./MockData";
import { fmt } from "./Utils/Utilities";
import { Badge, ActionMenu, Modal, Confirm } from "./SharedUI";

function CustomersPage({ showToast: toast }) {
  const [customers, setCustomers] = useState(MOCK.customers);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [filterType, setFilterType] = useState("All");
  const [modal, setModal] = useState(null); 
  const [confirm, setConfirm] = useState(null);
  const [form, setForm] = useState({
    name: "",
    type: "Commercial",
    phone: "",
    email: "",
    creditLimit: "",
    accountManager: "",
  });

  const filtered = customers.filter((c) => {
    const q = search.toLowerCase();
    const matchQ =
      c.name.toLowerCase().includes(q) ||
      c.id.toLowerCase().includes(q) ||
      c.email.toLowerCase().includes(q);
    const matchS = filterStatus === "All" || c.status === filterStatus;
    const matchT = filterType === "All" || c.type === filterType;
    return matchQ && matchS && matchT;
  });

  const openCreate = () => {
    setForm({
      name: "",
      type: "Commercial",
      phone: "",
      email: "",
      creditLimit: "",
      accountManager: "",
    });
    setModal("create");
  };

  const openEdit = (c) => {
    setForm({
      name: c.name,
      type: c.type,
      phone: c.phone,
      email: c.email,
      creditLimit: c.creditLimit,
      accountManager: c.accountManager,
    });
    setModal({ edit: c });
  };

  const handleSave = () => {
    if (!form.name) return toast("Customer name is required", "e");
    if (modal === "create") {
      const newC = {
        ...form,
        id: `C${String(customers.length + 1).padStart(3, "0")}`,
        balance: 0,
        status: "Active",
        lastOrder: null,
        creditLimit: Number(form.creditLimit) || 0,
      };
      setCustomers((p) => [...p, newC]);
      toast("Customer created successfully", "s");
      // API: API.customers.create(form)
    } else {
      setCustomers((p) =>
        p.map((c) =>
          c.id === modal.edit.id
            ? {
                ...c,
                ...form,
                creditLimit: Number(form.creditLimit) || c.creditLimit,
              }
            : c,
        ),
      );
      toast("Customer updated", "s");
      // API: API.customers.update(modal.edit.id, form)
    }
    setModal(null);
  };

  const handleDelete = (c) => setConfirm(c);
  const confirmDelete = () => {
    setCustomers((p) => p.filter((c) => c.id !== confirm.id));
    toast("Customer removed", "w");
    // API: API.customers.delete(confirm.id)
    setConfirm(null);
  };

  const types = ["All", ...new Set(MOCK.customers.map((c) => c.type))];

  return (
    <div className="page">
      <div className="panel">
        <div className="tbar">
          <div className="tsearch">
            <i className="bi bi-search tc3" />
            <input
              placeholder="Search customers…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className="tfilter"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            {["All", "Active", "Overdue", "Blocked"].map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
          <select
            className="tfilter"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            {types.map((t) => (
              <option key={t}>{t}</option>
            ))}
          </select>
          <button className="btn btn-p" onClick={openCreate}>
            <i className="bi bi-plus-lg" /> New Customer
          </button>
          <span className="fs11 tc3 mono" style={{ marginLeft: "auto" }}>
            {filtered.length} records
          </span>
        </div>
        <table className="tbl">
          <thead>
            <tr>
              <th>ID</th>
              <th>Customer</th>
              <th>Type</th>
              <th>Balance</th>
              <th>Credit Limit</th>
              <th>Last Order</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id}>
                <td className="mono t-flame">{c.id}</td>
                <td>
                  <div className="fs12 fw6">{c.name}</div>
                  <div className="fs10 tc3">{c.email}</div>
                </td>
                <td>
                  <span className="tag">{c.type}</span>
                </td>
                <td
                  className={`mono fw7 ${c.status === "Overdue" ? "t-danger" : ""}`}
                >
                  {fmt.currency(c.balance)}
                </td>
                <td className="mono tc2">{fmt.currency(c.creditLimit)}</td>
                <td className="fs11 tc2 mono">{fmt.date(c.lastOrder)}</td>
                <td>
                  <Badge status={c.status} />
                </td>
                <td>
                  <ActionMenu
                    items={[
                      {
                        icon: <i className="bi bi-pencil" />,
                        label: "Edit",
                        action: () => openEdit(c),
                      },
                      {
                        icon: <i className="bi bi-clipboard" />,
                        label: "View Account",
                        action: () => toast(`Viewing ${c.name}`, "s"),
                      },
                      {
                        icon: <i className="bi bi-trash" />,
                        label: "Delete",
                        danger: true,
                        action: () => handleDelete(c),
                      },
                    ]}
                  />
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={9}>
                  <div className="empty">
                    <div className="empty-ico">
                      <i className="bi bi-search" />
                    </div>
                    No customers found
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {modal && (
        <Modal
          title={
            modal === "create" ? "New Customer" : `Edit — ${modal.edit.name}`
          }
          onClose={() => setModal(null)}
          footer={
            <>
              <button className="btn btn-g" onClick={() => setModal(null)}>
                Cancel
              </button>
              <button className="btn btn-p" onClick={handleSave}>
                Save Customer
              </button>
            </>
          }
        >
          <div className="frow">
            <div className="fg" style={{ gridColumn: "1/-1" }}>
              <label className="fl">Company Name *</label>
              <input
                className="fi"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Enter company name"
              />
            </div>
          </div>
          <div className="frow">
            <div className="fg">
              <label className="fl">Type</label>
              <select
                className="fsel"
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
              >
                {[
                  "Commercial",
                  "Industrial",
                  "Retail",
                  "Government",
                  "Agricultural",
                ].map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="frow">
            <div className="fg">
              <label className="fl">Phone</label>
              <input
                className="fi"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+1-555-0000"
              />
            </div>
            <div className="fg">
              <label className="fl">Email</label>
              <input
                className="fi"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="billing@company.com"
              />
            </div>
          </div>
          <div className="frow">
            <div className="fg">
              <label className="fl">Credit Limit (ZAR)</label>
              <input
                className="fi"
                type="number"
                value={form.creditLimit}
                onChange={(e) =>
                  setForm({ ...form, creditLimit: e.target.value })
                }
                placeholder="50000"
              />
            </div>
            <div className="fg">
              <label className="fl">Account Manager</label>
              <input
                className="fi"
                value={form.accountManager}
                onChange={(e) =>
                  setForm({ ...form, accountManager: e.target.value })
                }
                placeholder="Agent name"
              />
            </div>
          </div>
        </Modal>
      )}
      {confirm && (
        <Confirm
          message={`Delete "${confirm.name}"? This action cannot be undone.`}
          onConfirm={confirmDelete}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

export default CustomersPage;
